/**
 * PartsQueue — one-stop dashboard of every WO waiting on / ordered / received
 * / backordered parts. Techs and admins can flip a WO's status inline.
 *
 * Backend:
 *   GET  /api/parts-queue                     — list, grouped by status
 *   POST /api/jobs/{id}/parts-status          — update
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Wrench, ArrowsClockwise, MagnifyingGlass, Warning } from "@phosphor-icons/react";

const STATUS_ORDER = ["waiting", "ordered", "backordered", "received"];
const STATUS_TINT = {
  waiting:     { bg: "bg-amber-100",   text: "text-amber-800",  ring: "border-amber-300",  pill: "bg-amber-500" },
  ordered:     { bg: "bg-cyan-100",    text: "text-cyan-800",   ring: "border-cyan-300",   pill: "bg-cyan-600" },
  backordered: { bg: "bg-red-100",     text: "text-red-800",    ring: "border-red-300",    pill: "bg-red-600" },
  received:    { bg: "bg-emerald-100", text: "text-emerald-800",ring: "border-emerald-300",pill: "bg-emerald-600" },
};

const daysSince = (iso) => {
  if (!iso) return 0;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return 0;
    return Math.floor((Date.now() - d.getTime()) / 86400000);
  } catch { return 0; }
};

export default function PartsQueue() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/parts-queue");
      setData(data);
    } catch { setData({ counts: {}, labels: {}, buckets: {}, rows: [] }); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const labels = data?.labels || {};
  const rows = data?.rows || [];
  const needle = q.trim().toLowerCase();
  const filteredRows = useMemo(() => {
    if (!needle) return rows;
    return rows.filter(r =>
      (r.work_order_number || "").toLowerCase().includes(needle) ||
      (r.property_address || "").toLowerCase().includes(needle) ||
      (r.title || "").toLowerCase().includes(needle) ||
      (r.parts_note || "").toLowerCase().includes(needle) ||
      (r.assigned_to_name || "").toLowerCase().includes(needle)
    );
  }, [rows, needle]);

  const buckets = STATUS_ORDER.reduce((acc, s) => {
    acc[s] = filteredRows.filter(r => r.parts_status === s);
    return acc;
  }, {});

  return (
    <AdminShell>
      <div className="p-4 sm:p-8" data-testid="parts-queue-page">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl inline-flex items-center gap-2">
              <Wrench weight="fill" size={28}/> Parts Queue
            </h1>
            <p className="mt-1 text-sm text-slate-600 font-medium">
              Every WO waiting on special-order parts. Update status in one tap.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button onClick={() => setAddOpen(true)}
              data-testid="parts-queue-add-btn"
              className="h-11 px-3 border-2 border-[#0f172a] bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-1.5">
              + Add WO
            </button>
            <button onClick={load} disabled={busy}
              data-testid="parts-queue-refresh"
              className="h-11 px-3 border-2 border-[#0f172a] bg-white hover:bg-slate-100 font-black uppercase text-xs tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50">
              <ArrowsClockwise weight="bold" size={14}/> {busy ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>

        {addOpen && (
          <AddWoToQueueModal
            onClose={() => setAddOpen(false)}
            onAdded={async () => { setAddOpen(false); await load(); }}
          />
        )}

        <div className="mt-4 flex items-center gap-2">
          <div className="flex-1 relative">
            <MagnifyingGlass weight="bold" size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search WO#, address, tech, or note…"
              data-testid="parts-queue-search"
              className="w-full h-11 pl-8 pr-3 border-2 border-slate-300 text-sm bg-white"/>
          </div>
          <span className="text-xs text-slate-500 font-black uppercase tracking-widest whitespace-nowrap">
            {filteredRows.length} total
          </span>
        </div>

        {rows.length === 0 && !busy && (
          <div className="mt-8 p-10 text-center rugged-card bg-white">
            <div className="text-sm font-semibold text-slate-500">
              Parts Queue is empty — no WOs are waiting on ordered parts. Set a status on any WO to add it here.
            </div>
          </div>
        )}

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {STATUS_ORDER.map(s => {
            const t = STATUS_TINT[s];
            const list = buckets[s] || [];
            return (
              <div key={s} className={`rugged-card bg-white p-4 ${t.ring} border-2`} data-testid={`parts-bucket-${s}`}>
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="font-black uppercase tracking-widest text-sm inline-flex items-center gap-2">
                    <span className={`inline-block h-2.5 w-2.5 rounded-full ${t.pill}`}/>
                    {labels[s] || s}
                  </div>
                  <span className={`inline-flex items-center justify-center h-6 min-w-6 px-2 rounded-full text-xs font-black ${t.pill} text-white`} data-testid={`parts-bucket-count-${s}`}>
                    {list.length}
                  </span>
                </div>
                {list.length === 0 ? (
                  <div className="text-xs text-slate-400 italic py-4">No WOs.</div>
                ) : (
                  <ul className="space-y-2">
                    {list.map(r => (
                      <li key={r.id} data-testid={`parts-row-${r.id}`}
                        className="p-3 border-2 border-slate-200 rounded-lg hover:border-[#00C4CC] transition-colors">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="min-w-0">
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                              WO {r.work_order_number || r.id.slice(-6)}
                            </div>
                            <Link to={`/admin/jobs/${r.id}`} data-testid={`parts-row-link-${r.id}`}
                              className="mt-0.5 block font-semibold text-slate-900 hover:text-cyan-700 truncate">
                              {r.property_address || r.title || "—"}
                              {r.unit ? ` · Unit ${r.unit}` : ""}
                            </Link>
                            {r.assigned_to_name && (
                              <div className="text-[11px] text-slate-500 mt-0.5">
                                Tech: <b>{r.assigned_to_name}</b>
                              </div>
                            )}
                          </div>
                          <DaysBadge days={daysSince(r.parts_status_at)}/>
                        </div>
                        {r.parts_note && (
                          <div className={`mt-2 p-2 text-xs rounded ${t.bg} ${t.text}`} data-testid={`parts-row-note-${r.id}`}>
                            📝 {r.parts_note}
                          </div>
                        )}
                        <div className="mt-2 text-[10px] text-slate-500">
                          {r.parts_status_by?.name ? <>Updated by <b>{r.parts_status_by.name}</b></> : "Updated"}
                          {r.parts_status_at ? ` · ${new Date(r.parts_status_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : ""}
                        </div>
                        <div className="mt-2">
                          <PartsStatusPicker
                            job={r}
                            currentStatus={r.parts_status}
                            currentNote={r.parts_note || ""}
                            onSaved={load}
                            compact
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </AdminShell>
  );
}

/** Red badge if the WO has been in-status for more than 7 days */
function DaysBadge({ days }) {
  const overdue = days >= 7;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${overdue ? "bg-red-600 text-white" : "bg-slate-100 text-slate-600"}`}
      data-testid="parts-days-badge">
      {overdue && <Warning weight="fill" size={10} className="mr-1"/>}
      {days === 0 ? "New today" : `${days}d`}
    </span>
  );
}

/**
 * PartsStatusPicker — reusable dropdown + note popover. Used on this page
 * inline, on the Admin Job Desktop card, and inside Worker Job View.
 *
 * Props:
 *   job:            the job object (needs .id)
 *   currentStatus:  string | null
 *   currentNote:    string
 *   onSaved:        callback after successful save
 *   compact:        smaller pill styling for embedded use
 */
export function PartsStatusPicker({ job, currentStatus, currentNote = "", onSaved, compact = false }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(currentStatus || "");
  const [note, setNote] = useState(currentNote);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setStatus(currentStatus || ""); setNote(currentNote || ""); }, [currentStatus, currentNote]);

  const save = async (nextStatus) => {
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/parts-status`, {
        status: nextStatus, note: note,
      });
      toast.success(nextStatus ? `Status set to ${nextStatus === "waiting" ? "Waiting on Parts" : nextStatus === "ordered" ? "Parts Ordered" : nextStatus === "backordered" ? "Backordered" : "Received"}` : "Parts status cleared");
      setOpen(false);
      onSaved && await onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't save parts status");
    } finally { setBusy(false); }
  };

  const labels = {
    waiting:     "Waiting on Parts",
    ordered:     "Parts Ordered",
    backordered: "Backordered",
    received:    "Received · Ready",
  };
  const pillClass = currentStatus === "waiting" ? "bg-amber-500 text-white"
                   : currentStatus === "ordered" ? "bg-cyan-600 text-white"
                   : currentStatus === "backordered" ? "bg-red-600 text-white"
                   : currentStatus === "received" ? "bg-emerald-600 text-white"
                   : "bg-slate-100 text-slate-700 border-2 border-dashed border-slate-300";

  return (
    <div className="relative inline-block" data-testid="parts-status-picker">
      <button type="button" onClick={() => setOpen(o => !o)}
        data-testid="parts-status-toggle"
        className={`${compact ? "h-8 px-2.5 text-[10px]" : "h-9 px-3 text-[11px]"} font-black uppercase tracking-widest inline-flex items-center gap-1.5 ${pillClass}`}>
        🔧 {currentStatus ? labels[currentStatus] : "Set parts status"}
      </button>
      {open && (
        <div className="absolute z-50 mt-1 right-0 w-72 bg-white border-2 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] p-3" data-testid="parts-status-popover">
          <div className="space-y-1.5">
            {["waiting", "ordered", "backordered", "received"].map(s => (
              <button key={s} type="button" onClick={() => { setStatus(s); }}
                data-testid={`parts-pick-${s}`}
                className={`w-full text-left h-9 px-2 text-xs font-black uppercase tracking-widest ${status === s ? "bg-cyan-600 text-white" : "bg-slate-50 hover:bg-slate-100 text-slate-800"}`}>
                {labels[s]}
              </button>
            ))}
          </div>
          <label className="block mt-3">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Note (optional)</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Ferguson PO#12345, ETA Sept 25"
              maxLength={500} rows={2}
              data-testid="parts-status-note"
              className="mt-1 w-full border-2 border-slate-300 text-sm p-2"/>
          </label>
          <div className="mt-3 flex items-center gap-2">
            <button type="button" onClick={() => save(status)} disabled={busy || !status}
              data-testid="parts-status-save"
              className="flex-1 h-9 bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest disabled:opacity-40">
              {busy ? "Saving…" : "Save"}
            </button>
            {currentStatus && (
              <button type="button" onClick={() => save("")} disabled={busy}
                data-testid="parts-status-clear"
                className="h-9 px-3 border-2 border-red-600 text-red-700 font-black uppercase text-[10px] tracking-widest hover:bg-red-50 disabled:opacity-40">
                Clear
              </button>
            )}
            <button type="button" onClick={() => setOpen(false)}
              data-testid="parts-status-cancel"
              className="h-9 px-3 border-2 border-slate-300 font-black uppercase text-[10px] tracking-widest hover:bg-slate-100">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * AddWoToQueueModal — searches WOs by number, address, tech, then flips
 * `parts_status` on the picked one in a single click. Debounced typeahead.
 */
function AddWoToQueueModal({ onClose, onAdded }) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [status, setStatus] = useState("waiting");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  // Debounced search — pulls first 25 non-completed jobs matching term.
  useEffect(() => {
    const t = (term || "").trim();
    if (!t) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/jobs`, { params: { q: t, limit: 25 } });
        if (!cancelled) setResults(Array.isArray(data) ? data : []);
      } catch { if (!cancelled) setResults([]); }
      finally { if (!cancelled) setLoading(false); }
    }, 260);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [term]);

  const submit = async () => {
    if (!picked || !status) return;
    setBusy(true);
    try {
      await api.post(`/jobs/${picked.id}/parts-status`, { status, note });
      toast.success("Added to Parts Queue");
      onAdded && await onAdded();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't add WO");
    } finally { setBusy(false); }
  };

  const labels = {
    waiting:     "Waiting on Parts",
    ordered:     "Parts Ordered",
    backordered: "Backordered",
    received:    "Received · Ready",
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-start sm:items-center justify-center z-[110] p-4 overflow-y-auto"
         onClick={onClose} data-testid="parts-add-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a] p-6 my-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-black text-2xl inline-flex items-center gap-2">
              <Wrench weight="fill" size={22}/> Add WO to Parts Queue
            </h3>
            <p className="mt-1 text-xs text-slate-600 font-medium">
              Search by WO#, address, or tech, then pick a status + optional note.
            </p>
          </div>
          <button onClick={onClose} className="p-1" data-testid="parts-add-close">✕</button>
        </div>

        {/* Step 1 · Pick a WO */}
        {!picked && (
          <div className="mt-4">
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">1 · Find the WO</span>
              <input value={term} onChange={(e) => setTerm(e.target.value)}
                placeholder="WO number, address, unit, tech name…"
                autoFocus
                data-testid="parts-add-search"
                className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] text-sm bg-white"/>
            </label>
            <div className="mt-3 max-h-80 overflow-y-auto border-2 border-slate-200" data-testid="parts-add-results">
              {loading && <div className="p-3 text-sm text-slate-500">Searching…</div>}
              {!loading && term.trim() && results.length === 0 && (
                <div className="p-3 text-sm text-slate-500">No matches. Try a WO#, address, or tech.</div>
              )}
              {!loading && !term.trim() && (
                <div className="p-3 text-sm text-slate-400 italic">Start typing to search…</div>
              )}
              {!loading && results.map(r => (
                <button key={r.id} type="button" onClick={() => setPicked(r)}
                  data-testid={`parts-add-result-${r.id}`}
                  className="w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 hover:bg-cyan-50">
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    WO {r.work_order_number || r.id.slice(-6)}
                    {r.parts_status && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-amber-500 text-white text-[9px]">
                        already: {r.parts_status}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 font-semibold text-slate-900 truncate">
                    {r.property_address || r.title || "—"}
                    {r.unit ? ` · Unit ${r.unit}` : ""}
                  </div>
                  {r.assigned_to_name && (
                    <div className="text-[11px] text-slate-500 mt-0.5">Tech: <b>{r.assigned_to_name}</b></div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2 · Set status + note */}
        {picked && (
          <div className="mt-4 space-y-3" data-testid="parts-add-step2">
            <div className="p-3 border-2 border-cyan-400 bg-cyan-50">
              <div className="text-[10px] font-black uppercase tracking-widest text-cyan-800">Picked</div>
              <div className="mt-0.5 font-semibold text-slate-900">
                WO {picked.work_order_number || picked.id.slice(-6)} · {picked.property_address || picked.title}
              </div>
              <button type="button" onClick={() => setPicked(null)}
                data-testid="parts-add-change"
                className="mt-1 text-[10px] font-black uppercase tracking-widest text-cyan-800 underline">
                Change
              </button>
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">2 · Status</span>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {["waiting", "ordered", "backordered", "received"].map(s => (
                  <button key={s} type="button" onClick={() => setStatus(s)}
                    data-testid={`parts-add-pick-${s}`}
                    className={`h-10 px-3 text-xs font-black uppercase tracking-widest ${status === s ? "bg-cyan-600 text-white" : "bg-slate-50 hover:bg-slate-100 text-slate-800 border border-slate-200"}`}>
                    {labels[s]}
                  </button>
                ))}
              </div>
            </div>
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">3 · Note (optional)</span>
              <textarea value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Ferguson PO#12345, ETA Sept 25"
                maxLength={500} rows={2}
                data-testid="parts-add-note"
                className="mt-1 w-full border-2 border-slate-300 text-sm p-2"/>
            </label>
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
            className="h-11 px-4 border-2 border-slate-300 font-black uppercase text-xs tracking-widest hover:bg-slate-100"
            data-testid="parts-add-cancel">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={!picked || !status || busy}
            className="h-11 px-5 bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest disabled:opacity-40"
            data-testid="parts-add-save">
            {busy ? "Adding…" : "Add to Queue"}
          </button>
        </div>
      </div>
    </div>
  );
}

